import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Lock, LogOut, Search, Download, Inbox, ShieldCheck, RefreshCw } from 'lucide-react';
import '../styles/rivera.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = 'rivera_admin_token';

const Admin = () => {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || '');
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);
  const [creds, setCreds] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [tab, setTab] = useState('inquiries');
  const [inquiries, setInquiries] = useState([]);
  const [requests, setRequests] = useState([]);
  const [query, setQuery] = useState('');

  const authHeader = useCallback(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [c, v] = await Promise.all([
        axios.get(`${API}/contact-submissions`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/vault-requests`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setInquiries(c.data);
      setRequests(v.data);
    } catch (e) {
      if (e.response?.status === 401) handleLogout();
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    const verify = async () => {
      if (!token) { setChecking(false); return; }
      try {
        await axios.get(`${API}/admin/me`, { headers: { Authorization: `Bearer ${token}` } });
        setAuthed(true);
      } catch {
        localStorage.removeItem(TOKEN_KEY);
        setToken('');
      } finally {
        setChecking(false);
      }
    };
    verify();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (authed) loadData();
  }, [authed, loadData]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, creds);
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      setAuthed(true);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken('');
    setAuthed(false);
  };

  const downloadFile = async (id, name) => {
    try {
      const res = await axios.get(`${API}/admin/download/${id}`, { headers: authHeader(), responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = name || 'attachment';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      alert('Unable to download file.');
    }
  };

  const fmt = (ts) => {
    try { return new Date(ts).toLocaleString(); } catch { return ts; }
  };

  const q = query.trim().toLowerCase();
  const filteredInquiries = inquiries.filter((i) =>
    !q || [i.name, i.email, i.organization, i.inquiry_type, i.message].some((f) => (f || '').toLowerCase().includes(q))
  );
  const filteredRequests = requests.filter((r) =>
    !q || [r.email, r.reason].some((f) => (f || '').toLowerCase().includes(q))
  );

  if (checking) {
    return <div className="admin-shell"><div className="admin-loading">Loading…</div></div>;
  }

  if (!authed) {
    return (
      <div className="admin-shell admin-login-shell">
        <form className="admin-login-card" onSubmit={handleLogin} data-testid="admin-login-form">
          <div className="admin-login-icon"><Lock size={28} /></div>
          <h1 className="admin-login-title">Rivera Industries</h1>
          <p className="admin-login-sub">Admin Console · Secure Access</p>
          <label>Email</label>
          <input
            type="email" required value={creds.email}
            onChange={(e) => setCreds({ ...creds, email: e.target.value })}
            placeholder="admin@riveraind.com" data-testid="admin-email"
          />
          <label>Password</label>
          <input
            type="password" required value={creds.password}
            onChange={(e) => setCreds({ ...creds, password: e.target.value })}
            placeholder="••••••••" data-testid="admin-password"
          />
          {error && <div className="admin-error" data-testid="admin-login-error">{error}</div>}
          <button type="submit" className="rivera-btn-submit" disabled={loading} data-testid="admin-login-submit">
            {loading ? 'Signing in…' : 'Sign In'}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="admin-shell" data-testid="admin-dashboard">
      <header className="admin-header">
        <div className="admin-brand">
          <ShieldCheck size={22} />
          <span>RIVERA INDUSTRIES · ADMIN</span>
        </div>
        <button className="admin-logout" onClick={handleLogout} data-testid="admin-logout">
          <LogOut size={16} /> Logout
        </button>
      </header>

      <div className="admin-toolbar">
        <div className="admin-tabs">
          <button
            className={`admin-tab ${tab === 'inquiries' ? 'active' : ''}`}
            onClick={() => setTab('inquiries')} data-testid="tab-inquiries"
          >
            <Inbox size={16} /> Inquiries <span className="admin-count">{inquiries.length}</span>
          </button>
          <button
            className={`admin-tab ${tab === 'requests' ? 'active' : ''}`}
            onClick={() => setTab('requests')} data-testid="tab-requests"
          >
            <Lock size={16} /> Portal Requests <span className="admin-count">{requests.length}</span>
          </button>
        </div>
        <div className="admin-search">
          <Search size={16} />
          <input
            value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Search…" data-testid="admin-search"
          />
        </div>
        <button className="admin-refresh" onClick={loadData} title="Refresh" data-testid="admin-refresh">
          <RefreshCw size={16} className={loading ? 'spin' : ''} />
        </button>
      </div>

      <main className="admin-main">
        {tab === 'inquiries' ? (
          <div className="admin-table-wrap">
            <table className="admin-table" data-testid="inquiries-table">
              <thead>
                <tr>
                  <th>Date</th><th>Name</th><th>Email</th><th>Organization</th>
                  <th>Type</th><th>Message</th><th>Attachment</th>
                </tr>
              </thead>
              <tbody>
                {filteredInquiries.length === 0 && (
                  <tr><td colSpan="7" className="admin-empty">No inquiries found.</td></tr>
                )}
                {filteredInquiries.map((i) => (
                  <tr key={i.id}>
                    <td className="admin-nowrap">{fmt(i.timestamp)}</td>
                    <td>{i.name}</td>
                    <td><a href={`mailto:${i.email}`} className="admin-link">{i.email}</a></td>
                    <td>{i.organization || '—'}</td>
                    <td><span className="admin-badge">{i.inquiry_type}</span></td>
                    <td className="admin-msg">{i.message}</td>
                    <td>
                      {i.file_name ? (
                        <button className="admin-dl" onClick={() => downloadFile(i.id, i.file_name)} data-testid={`download-${i.id}`}>
                          <Download size={14} /> {i.file_name}
                        </button>
                      ) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="admin-table-wrap">
            <table className="admin-table" data-testid="requests-table">
              <thead>
                <tr><th>Date</th><th>Email</th><th>Reason</th></tr>
              </thead>
              <tbody>
                {filteredRequests.length === 0 && (
                  <tr><td colSpan="3" className="admin-empty">No portal requests found.</td></tr>
                )}
                {filteredRequests.map((r) => (
                  <tr key={r.id}>
                    <td className="admin-nowrap">{fmt(r.timestamp)}</td>
                    <td><a href={`mailto:${r.email}`} className="admin-link">{r.email}</a></td>
                    <td className="admin-msg">{r.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
};

export default Admin;
